import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { LeadTag } from "@/types/lead";
import { dispatchConversationLeadPatch } from "@/lib/conversation-lead-patch";
import { subscribePostgresHub } from "@/lib/realtime-channel";

interface UseLeadTagsOptions {
  workspaceId: string;
  leadId?: string;
}

export function useLeadTags({ workspaceId, leadId }: UseLeadTagsOptions) {
  const [workspaceTags, setWorkspaceTags] = useState<LeadTag[]>([]);
  const [leadTags, setLeadTags] = useState<LeadTag[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchWorkspaceTags = useCallback(async () => {
    if (!workspaceId) return;

    const { data, error } = await supabase
      .from("lead_tags")
      .select("id, name, color")
      .eq("workspace_id", workspaceId)
      .order("name");

    if (error) {
      console.error("Error fetching workspace tags:", error);
      return;
    }

    setWorkspaceTags(data || []);
  }, [workspaceId]);

  const fetchLeadTags = useCallback(async () => {
    if (!leadId) {
      setLeadTags([]);
      return;
    }

    const { data, error } = await supabase
      .from("lead_tag_assignments")
      .select(`
        tag:tag_id (
          id,
          name,
          color
        )
      `)
      .eq("lead_id", leadId);

    if (error) {
      console.error("Error fetching lead tags:", error);
      return;
    }

    const tags = data?.map((item: any) => item.tag).filter(Boolean) || [];
    setLeadTags(tags);
  }, [leadId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchWorkspaceTags(), fetchLeadTags()]).finally(() => {
      setLoading(false);
    });
  }, [fetchWorkspaceTags, fetchLeadTags]);

  // Realtime: tags do workspace (hub — Conversations + LeadTagSelector montam juntos)
  useEffect(() => {
    if (!workspaceId) return;

    return subscribePostgresHub(
      `workspace-tags-${workspaceId}`,
      {
        event: "*",
        schema: "public",
        table: "lead_tags",
        filter: `workspace_id=eq.${workspaceId}`,
      },
      () => {
        fetchWorkspaceTags();
      },
    );
  }, [workspaceId, fetchWorkspaceTags]);

  // Realtime: atribuições de tags por lead (hub — LeadTagsPopover + LeadTagSelector)
  useEffect(() => {
    if (!leadId) return;

    return subscribePostgresHub(
      `lead-tags-${leadId}`,
      {
        event: "*",
        schema: "public",
        table: "lead_tag_assignments",
      },
      (payload) => {
        if (payload.new && (payload.new as { lead_id?: string }).lead_id === leadId) {
          fetchLeadTags();
        } else if (payload.old && (payload.old as { lead_id?: string }).lead_id === leadId) {
          fetchLeadTags();
        }
      },
    );
  }, [leadId, fetchLeadTags]);

  const createTag = async (name: string, color: string): Promise<LeadTag | null> => {
    const trimmed = name.trim();
    const existingTag = workspaceTags.find(
      (t) => t.name.toLowerCase() === trimmed.toLowerCase() && !t.id.startsWith("temp-"),
    );

    if (existingTag) {
      toast({
        title: "Esta tag já existe",
        description: "Selecione-a na lista.",
        variant: "destructive",
      });
      return existingTag;
    }

    const tempId = `temp-${crypto.randomUUID()}`;
    const optimistic: LeadTag = { id: tempId, name: trimmed, color };
    setWorkspaceTags((prev) =>
      [...prev, optimistic].sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    );

    const { data, error } = await supabase
      .from("lead_tags")
      .insert({
        workspace_id: workspaceId,
        name: trimmed,
        color,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating tag:", error);
      setWorkspaceTags((prev) => prev.filter((t) => t.id !== tempId));
      toast({
        title: "Erro ao criar tag",
        description: error.message,
        variant: "destructive",
      });
      return null;
    }

    setWorkspaceTags((prev) =>
      prev
        .map((t) => (t.id === tempId ? data : t))
        .sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    );
    return data;
  };

  const assignTagToLead = async (tagId: string) => {
    if (!leadId) return false;
    if (tagId.startsWith("temp-")) return false;

    if (leadTags.some((t) => t.id === tagId)) {
      return true;
    }

    const tag = workspaceTags.find((t) => t.id === tagId);
    if (tag) {
      setLeadTags((prev) => {
        const next = [...prev, tag];
        dispatchConversationLeadPatch({
          leadId,
          leadTags: next,
          tagIds: next.map((t) => t.id),
        });
        return next;
      });
    }

    const { error } = await supabase.from("lead_tag_assignments").insert({
      lead_id: leadId,
      tag_id: tagId,
    });

    if (error) {
      console.error("Error assigning tag:", error);
      if (tag) {
        setLeadTags((prev) => {
          const reverted = prev.filter((t) => t.id !== tagId);
          dispatchConversationLeadPatch({
            leadId,
            leadTags: reverted,
            tagIds: reverted.map((t) => t.id),
          });
          return reverted;
        });
      }
      toast({
        title: "Erro ao adicionar tag",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const removeTagFromLead = async (tagId: string) => {
    if (!leadId) return false;

    const previousTags = leadTags;
    const nextTags = leadTags.filter((t) => t.id !== tagId);
    setLeadTags(nextTags);
    dispatchConversationLeadPatch({
      leadId,
      leadTags: nextTags,
      tagIds: nextTags.map((t) => t.id),
    });

    const { error } = await supabase
      .from("lead_tag_assignments")
      .delete()
      .eq("lead_id", leadId)
      .eq("tag_id", tagId);

    if (error) {
      console.error("Error removing tag:", error);
      setLeadTags(previousTags);
      dispatchConversationLeadPatch({
        leadId,
        leadTags: previousTags,
        tagIds: previousTags.map((t) => t.id),
      });
      toast({
        title: "Erro ao remover tag",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const updateTag = async (tagId: string, name: string, color: string) => {
    const trimmed = name.trim();
    if (!trimmed) return false;

    const duplicate = workspaceTags.find(
      (t) => t.id !== tagId && t.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (duplicate) {
      toast({
        title: "Esta tag já existe",
        description: "Escolha outro nome para a tag.",
        variant: "destructive",
      });
      return false;
    }

    const { error } = await supabase
      .from("lead_tags")
      .update({ name: trimmed, color })
      .eq("id", tagId);

    if (error) {
      console.error("Error updating tag:", error);
      toast({
        title: "Erro ao atualizar tag",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }

    setWorkspaceTags((prev) =>
      prev
        .map((t) => (t.id === tagId ? { ...t, name: trimmed, color } : t))
        .sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    );
    setLeadTags((prev) =>
      prev.map((t) => (t.id === tagId ? { ...t, name: trimmed, color } : t)),
    );

    if (leadId && leadTags.some((t) => t.id === tagId)) {
      const nextTags = leadTags.map((t) =>
        t.id === tagId ? { ...t, name: trimmed, color } : t,
      );
      dispatchConversationLeadPatch({
        leadId,
        leadTags: nextTags,
        tagIds: nextTags.map((t) => t.id),
      });
    }

    return true;
  };

  const deleteTag = async (tagId: string) => {
    const { error } = await supabase.from("lead_tags").delete().eq("id", tagId);

    if (error) {
      console.error("Error deleting tag:", error);
      toast({
        title: "Erro ao excluir tag",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }

    setWorkspaceTags((prev) => prev.filter((t) => t.id !== tagId));
    setLeadTags((prev) => prev.filter((t) => t.id !== tagId));

    return true;
  };

  return {
    workspaceTags,
    leadTags,
    loading,
    createTag,
    assignTagToLead,
    removeTagFromLead,
    updateTag,
    deleteTag,
    refetch: () => {
      fetchWorkspaceTags();
      fetchLeadTags();
    },
  };
}
